/**
 * ViewTargetCameraComponent - wraps THREE.PerspectiveCamera for scene rendering with FOV,
 * near/far plane control, and world view target stack integration.
 */
import * as THREE from 'three';

import { EngineClass } from '../systems/ClassRegistry.js';
import { property } from '../utils/serialization/decorator.js';

import { SceneComponent } from './SceneComponent.js';

import type { SceneComponentOptions } from './SceneComponent.js';
import type { Actor } from '../actors/Actor.js';
import type { EditorPropertyChangedResult } from '../utils/EditorClassMeta.js';

export interface ViewTargetCameraComponentOptions extends SceneComponentOptions {
  /** Field of view in degrees */
  fov?: number;
  /** Aspect ratio (width/height) */
  aspect?: number;
  /** Near clipping plane distance */
  near?: number;
  /** Far clipping plane distance */
  far?: number;
  /** Whether this camera should become active on beginPlay */
  startActive?: boolean;
}

@EngineClass('ViewTargetCameraComponent')
export class ViewTargetCameraComponent extends SceneComponent {

  @property()
  protected fov: number = 75;

  @property()
  protected aspect: number = 1;

  @property()
  protected near: number = 0.1;

  @property()
  protected far: number = 1000;

  @property()
  protected startActive: boolean = false;

  /** The THREE.PerspectiveCamera object managed by this component */
  @property()
  protected camera!: THREE.PerspectiveCamera;

  protected cameraHelper: THREE.CameraHelper | null = null;

  public override initialize(options?: ViewTargetCameraComponentOptions): void {
    super.initialize(options);
    this.fov = options?.fov ?? this.fov;
    this.aspect = options?.aspect ?? this.aspect;
    this.near = options?.near ?? this.near;
    this.far = options?.far ?? this.far;
    this.startActive = options?.startActive ?? this.startActive;
    this.camera = new THREE.PerspectiveCamera(this.fov, this.aspect, this.near, this.far);
    this.add(this.camera);
    this.initializeFromProperties();
  }

  public override postLoad(): void {
    super.postLoad();
    this.initializeFromProperties();
  }

  public override beginPlay(): void {
    super.beginPlay();
    // If marked as startActive, set as override camera when play begins
    if (this.startActive) {
      this.setActive(true);
    }
  }

  public override endPlay(): void {
    super.endPlay();
    // Clear override camera if this was the active one
    if (this.isActive()) {
      this.setActive(false);
    }
  }

  /**
   * Initializes the camera from stored properties. Called after deserialization.
   */
  protected initializeFromProperties(): void {
    this.setFOV(this.fov);
    this.setAspect(this.aspect);
    this.setNear(this.near);
    this.setFar(this.far);
  }

  public getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  /**
   * @param fov - Field of view in degrees
   */
  public setFOV(fov: number): this {
    this.fov = fov;
    this.camera.fov = fov;
    this.camera.updateProjectionMatrix();
    this.recreateCameraHelper();
    return this;
  }

  public getFOV(): number {
    return this.camera.fov;
  }

  /**
   * @param aspect - Aspect ratio
   */
  public setAspect(aspect: number): this {
    this.aspect = aspect;
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
    this.recreateCameraHelper();
    return this;
  }

  public getAspect(): number {
    return this.camera.aspect;
  }

  /**
   * @param near - Near clipping plane distance
   */
  public setNear(near: number): this {
    this.near = near;
    this.camera.near = near;
    this.camera.updateProjectionMatrix();
    this.recreateCameraHelper();
    return this;
  }

  public getNear(): number {
    return this.camera.near;
  }

  /**
   * @param far - Far clipping plane distance
   */
  public setFar(far: number): this {
    this.far = far;
    this.camera.far = far;
    this.camera.updateProjectionMatrix();
    this.recreateCameraHelper();
    return this;
  }

  public getFar(): number {
    return this.camera.far;
  }

  /**
   * @param active - Whether this camera should be active
   */
  public setActive(active: boolean): this {
    const world = this.getWorld();
    if (!world) {
      console.warn('CameraComponent: Cannot set active - component not in world');
      return this;
    }

    if (active) {
      // Push this camera onto the view target stack
      world.pushViewTargetCamera(this.camera);
    } else {
      // Remove this camera from the view target stack (anywhere it may be)
      world.removeViewTargetCamera(this.camera);
    }

    return this;
  }

  public isActive(): boolean {
    const world = this.getWorld();
    if (!world) return false;
    const activeCamera = world.getActiveCamera();
    return activeCamera === this.camera;
  }

  /**
   * @param show - Whether to show the helper
   */
  public showHelper(show: boolean): this {
    if (show === !!this.cameraHelper) return this;
    if (show) {
      this.cameraHelper = new THREE.CameraHelper(this.camera);
    } else {
      this.disposeHelper();
    }
    return this;
  }

  /**
   * Detaches and disposes the camera helper. The helper is added directly
   * to `world.scene`, so without explicit cleanup the helper subtree (its
   * `Line` + `BufferGeometry` + `Material`) plus the renderer's cached
   * `RenderObject` and `dispose` listeners stay alive past actor removal.
   */
  protected disposeHelper(): void {
    if (!this.cameraHelper) return;
    this.cameraHelper.removeFromParent();
    this.cameraHelper.dispose();
    this.cameraHelper = null;
  }

  public isHelperVisible(): boolean {
    return !!this.cameraHelper;
  }

  protected recreateCameraHelper(): void {
    if (this.cameraHelper) {
      this.showHelper(false);
      this.showHelper(true);
    }
  }

  public override tickPrePhysics(deltaTime: number): void {
    super.tickPrePhysics(deltaTime);
    this.updateHelper();
  }

  protected updateHelper(): void {
    if (this.cameraHelper) {
      this.cameraHelper.visible = this.visible;
      if (this.cameraHelper.parent === null) {
        this.getWorld()?.scene.add(this.cameraHelper);
      }
      this.cameraHelper.update();
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorSelectedChanged(isSelected: boolean, selectedActors: Actor[]): void {
    super.onEditorSelectedChanged(isSelected, selectedActors);
    this.showHelper(isSelected);
    this.updateHelper();
  }

  public override onEditorRemoveFromWorld(): void {
    this.disposeHelper();
    super.onEditorRemoveFromWorld();
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);

    if (path === 'fov') this.setFOV(value);
    else if (path === 'aspect') this.setAspect(value);
    else if (path === 'near') this.setNear(value);
    else if (path === 'far') this.setFar(value);
    else if (path === 'startActive') this.setActive(value);

    this.updateHelper();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_Camera';
  }
}
